import React from 'react';
import { bool, number, objectOf, oneOf, shape, string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';

import { ResponsiveImage } from '../../../../components/index.js';

import css from './CustomAppearance.module.css';

// BackgroundImage doesn't have enforcable aspectratio
export const CustomAppearance = React.forwardRef((props, ref) => {
  const {
    className,
    rootClassName,
    backgroundColor,
    backgroundImage,
    backgroundImageOverlay,
    alt,
    sizes,
    priority,
  } = props;

  // Section/hero background images are full-bleed and rendered with an overlay + foreground
  // text, so they never need pixel-perfect sharpness. The Sharetribe/imgix hosted-asset
  // variants are signed (their `s=` param means width/format can't be re-transformed in code),
  // and for backgrounds come as 400 / 800 / 1200 / 2400 w. With sizes="100vw" a desktop
  // viewport (~1350px at DPR 1) needs >1200px, so the browser pulls the 2400w file — which is
  // exactly what PageSpeed's "Improve image delivery" flags (2400x1078 downloaded for a ~1335px
  // slot, ~140 KiB wasted on the hero alone). Cap the offered srcSet at 1200w so the oversized
  // 2400w variant is never selected; 1200w upscaled into a ~1335px overlaid background is
  // visually negligible, and dropping it also speeds up the LCP hero. There is no 1600w signed
  // variant, so 1200w is the practical ceiling. Fall back to all variants if none are <= the cap.
  const MAX_BACKGROUND_IMAGE_WIDTH = 1200;
  const withinCap = variant => (variant?.width || 0) <= MAX_BACKGROUND_IMAGE_WIDTH;

  const getVariantNames = img => {
    const { variants } = img?.attributes || {};
    if (!variants) return [];
    const names = Object.keys(variants);
    const capped = names.filter(name => withinCap(variants[name]));
    return capped.length ? capped : names;
  };
  const getVariantData = img => {
    const { variants } = img?.attributes || {};
    if (!variants) return [];
    const all = Object.values(variants).filter(Boolean);
    const capped = all.filter(withinCap);
    return capped.length ? capped : all;
  };

  const backgroundColorMaybe = backgroundColor ? { backgroundColor } : {};
  const imageSizes = sizes || '100vw';
  const imageVariants = getVariantData(backgroundImage);
  // Preload the largest variant we actually offer (within the cap) so the LCP preload matches
  // what the browser selects from imageSrcSet.
  const preloadVariant =
    imageVariants.reduce((acc, v) => ((v?.width || 0) > (acc?.width || 0) ? v : acc), null) ||
    imageVariants[0];
  const preloadSrcSet = imageVariants
    .map(variant => `${variant.url} ${variant.width}w`)
    .join(', ');

  // On top of the background image there could be an overlay that mixes in some color (e.g. black)
  // with the given opacity. Currently, there are 2 presets: "dark" and "darker".
  // At this point this is used as a shader to add contrast between foreground text and background.
  const { preset, color: overlayColor, opacity: overlayOpacity } = backgroundImageOverlay || {};
  const hasBackgroundOverlay = typeof preset === 'string' && preset !== 'none';
  const overlayStyle = hasBackgroundOverlay
    ? { backgroundColor: overlayColor, opacity: overlayOpacity }
    : {};

  const classes = classNames(rootClassName || css.backgroundImageWrapper, className);
  return (
    <div className={classes} style={backgroundColorMaybe}>
      {priority && preloadVariant?.url ? (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={preloadVariant.url}
            imageSrcSet={preloadSrcSet || undefined}
            imageSizes={imageSizes}
          />
        </Helmet>
      ) : null}
      {backgroundImage ? (
        <ResponsiveImage
          className={css.backgroundImage}
          ref={ref}
          alt={alt}
          image={backgroundImage}
          variants={getVariantNames(backgroundImage)}
          sizes={imageSizes}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchpriority={priority ? 'high' : undefined}
        />
      ) : null}
      {hasBackgroundOverlay ? <div className={css.backgroundOverlay} style={overlayStyle} /> : null}
    </div>
  );
});

CustomAppearance.displayName = 'CustomAppearance';

CustomAppearance.defaultProps = {
  rootClassName: null,
  className: null,
  alt: 'background image',
  sizes: null,
  priority: false,
  backgroundColor: null,
  backgroundImage: null,
  backgroundImageOverlay: null,
};

CustomAppearance.propTypes = {
  rootClassName: string,
  className: string,
  backgroundColor: string,
  backgroundImage: shape({
    id: string.isRequired,
    type: oneOf(['imageAsset']).isRequired,
    attributes: shape({
      variants: objectOf(
        shape({
          width: number.isRequired,
          height: number.isRequired,
          url: string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }),
  backgroundImageOverlay: shape({
    preset: string.isRequired,
    color: string.isRequired,
    opacity: number.isRequired,
  }),
  alt: string,
  sizes: string,
  priority: bool,
};
